import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { daysWaiting, isOverdue, type QueueItem } from '../../../lib/network-dashboard';
import { formatDate } from '../../../lib/i18n-format';

// ─── Una fila de la bandeja, igual para membresía y para CAP ────────────────
// Antes había dos componentes distintos y la fila de una propuesta CAP —la única
// que certifica un programa— era la que quedaba sin estado, sin antigüedad y sin
// botón para abrirla. Lo que cambia entre asuntos es el subtítulo, no la forma.

const STATUS_COLOR: Record<
  QueueItem['status'],
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  submitted: 'info',
  returned: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'default',
  superseded: 'default',
};

export function NetworkQueueRow({
  item,
  now,
  noticeDays,
  onOpen,
}: {
  item: QueueItem;
  now: Date;
  noticeDays: { formal?: number; advisory?: number } | undefined;
  onOpen: () => void;
}) {
  const { t } = useTranslation('sis');
  const late = item.status === 'submitted' && isOverdue(item, now, noticeDays);
  const days = daysWaiting(item, now);

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        py: 1.75,
        px: 2,
        borderLeft: '3px solid',
        borderLeftColor: late
          ? 'warning.main'
          : item.status === 'submitted'
            ? 'info.main'
            : 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            label={t(`network.dashboard.subject.${item.subject}`)}
          />
          <Typography sx={{ fontWeight: 600 }}>
            {item.subject === 'cap' ? item.title : t(`stages.${item.stage}`, { ns: 'membership' })}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t(`network.dashboard.kind.${item.kind}`)}
          {item.capCode ? ` · ${item.capCode} · ${t(`network.inbox.cap.level.${item.level}`)}` : ''}
          {` · ${t('network.inbox.cap.round', { round: item.round })}`}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 150 }}>
        <Typography variant="body2">{item.seminaryName}</Typography>
        {item.tmaiSiteAbbreviation && (
          <Typography variant="caption" color="text.secondary">
            {item.tmaiSiteAbbreviation}
          </Typography>
        )}
      </Box>

      <Chip
        size="small"
        color={STATUS_COLOR[item.status]}
        label={t(`network.inbox.status.${item.status}`)}
      />

      <Typography
        variant="body2"
        sx={{
          minWidth: 96,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: late ? 'warning.main' : 'text.secondary',
          fontWeight: late ? 700 : 400,
        }}
      >
        {item.status === 'submitted'
          ? t('network.dashboard.waiting', { days })
          : formatDate(item.decidedAt ?? item.submittedAt)}
      </Typography>

      <Button
        size="small"
        variant={item.status === 'submitted' ? 'contained' : 'text'}
        onClick={onOpen}
      >
        {t(item.status === 'submitted' ? 'network.dashboard.review' : 'network.inbox.open')}
      </Button>
    </Stack>
  );
}
